import { Component, Input, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { EnvironmentParameters } from 'app/environment/models/environment-parameters';
import { IGenomeTime } from 'bz-genome-angular/time-picker/time-picker/bzg-time-picker.component';
import { set } from 'lodash';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { MaintenanceService } from '../core/services/maintenance.service';
import { IJobData } from '../core/services/job-data.interface';
import { BzgModalService, BzgToastService } from 'bz-genome-angular';
import { I18nService } from 'i18n/i18n.service';
import { ConfirmationType } from 'app/core/models/confirmation-type.enum';
import { ActionConfirmationComponent } from 'app/shared/components/action-confirmation/action-confirmation.component';
import { DateUtil } from 'app/core/utils/DateUtil';

@Component({
  selector: 'biz-maintenance-cases',
  templateUrl: './maintenance-cases.component.html',
  styleUrls: ['./maintenance-cases.component.scss']
})
export class MaintenanceCasesComponent {
  @Input() parameters: EnvironmentParameters;
  public startTime: any;
  public initialIntervalStartTime: any;
  public endTime: any;
  public initialIntervalEndTime: any;
  public jobDataEnabled: boolean = true;
  public jobDataMode: string;
  public currentStatus: string = '';
  public _startTimeDebounce = new Subject();
  public _endTimeDebounce = new Subject();
  showLoading: boolean = false;
  isEditingMode: boolean = false;
  public stringValue = {
    hours: '12',
    minutes: '0',
    seconds: '0',
  };
  public limitValues = {
    hours: {
      max: 23,
      min: 0
    },
    minutes: {
      max: 59,
      min: 0
    },
    seconds: {
      max: 59,
      min: 0
    }
  }

  constructor(
    private maintenanceService: MaintenanceService,
    private _i18nService: I18nService,
    private _toastService: BzgToastService,
    private _modalService: BzgModalService,
  ) { }

  ngOnInit(): void {
    this.startTime = this._convertSecondsToDate(this.parameters.bizagiSchedulerInterval);
    this.initialIntervalStartTime = this._convertSecondsToDate(this.parameters.bizagiSchedulerInterval);
    this.endTime = this._convertSecondsToDate(this.parameters.bizagiSchedulerInterval);
    this.initialIntervalEndTime = this._convertSecondsToDate(this.parameters.bizagiSchedulerInterval);
    this.updateAndAdjustTimeValues(this._startTimeDebounce, this.startTime);
    this.updateAndAdjustTimeValues(this._endTimeDebounce, this.endTime);
    this.getCleanCasesJobData();
  }

  onDestory() {
    this._startTimeDebounce.complete();
    this._endTimeDebounce.complete();
  }

  public getCleanCasesJobData() {
    this.maintenanceService.getCleanCasesJobData().subscribe((jobData: IJobData) => {
      this.preloadTime(this.startTime, jobData.StartTime);
      this.preloadTime(this.endTime, jobData.EndTime);

      this.startTime.hours = String(DateUtil.calculateHoursToShowOrSave(this.startTime.hours, true)).padStart(2, '0');
      this.endTime.hours = String(DateUtil.calculateHoursToShowOrSave(this.endTime.hours, true)).padStart(2, '0');
      this.startTime.minutes = String(DateUtil.calculateMinutesToShowOrSave(this.startTime.minutes, true)).padStart(2, '0');
      this.endTime.minutes = String(DateUtil.calculateMinutesToShowOrSave(this.endTime.minutes, true)).padStart(2, '0');

      this.initialIntervalStartTime = { ...this.startTime };
      this.initialIntervalEndTime = { ...this.endTime };
      this.jobDataEnabled = jobData.Enabled;
      this.jobDataMode = jobData.Mode;
      this.setCurrentStatus();
    });
  }

  public preloadTime(time: any, currentTime: string) {
    time.hours = currentTime.split(':')[0];
    time.minutes = currentTime.split(':')[1];
  }

  public updateAndAdjustTimeValues(timeDebounce: any, time: any) {
    timeDebounce.pipe(debounceTime(1600)).subscribe((data: any) => {
      time[data.section] = 0;
      let validValue = Number(data.value);
      const minLimit = this.limitValues[data.section].min;
      const maxLimit = this.limitValues[data.section].max;

      if (validValue < minLimit) {
        validValue = minLimit;
      }

      else if (validValue > maxLimit) {
        validValue = maxLimit;
      }
      this.stringValue[data.section] = this.padForTwoDigits(validValue.toString());
      this.updateTime(time, data.section);
    });
  }

  public validMinimun(time: any) {
    let count = 0;
    Object.keys(time).forEach((key) => {
      if (!time[key]) {
        count++;
      }
    })

    if (count >= Object.keys(time).length) {
      time.seconds = 1;
    }
  }

  public updateTime(time: any, section: string) {
    time[section] = this.stringValue[section];
    this.validMinimun(time);
  }

  private _convertSecondsToDate(value: number): any {
    const hours = Math.floor(value / 3600);
    const minutes = Math.floor(value % 3600 / 60);
    const seconds = Math.floor(value % 3600 % 60);
    return {
      hours,
      minutes,
      seconds,
      period: ''
    }
  }

  public onlyNumbers(event: any) {
    const pattern = /^\d{1,2}/;
    const inputChar = String.fromCharCode(event.charCode);
    if (!pattern.test(inputChar)) {
      event.preventDefault();
    }
  };

  public checkValue(timeDebounce: any, section: string, value: string) {
    timeDebounce.next({ section, value });
  }

  public padForTwoDigits(stringvalue: string): string {
    return (stringvalue === '0' || (Number(stringvalue) < 10 && (stringvalue.indexOf('0') === -1)) ? '0' : '') + stringvalue;
  }

  public goUp(time: any, section: string) {
    if (this.isEditingMode) {
      let numberValue = Number(this.stringValue[section]);
      if (numberValue < this.limitValues[section].max) {
        numberValue = numberValue + 1;
      }
      this.stringValue[section] = this.padForTwoDigits(numberValue.toString());
      this.updateTime(time, section);
    }
  }

  public goDown(time: any, section: string) {
    if (this.isEditingMode) {
      let numberValue = Number(this.stringValue[section]);
      if (numberValue > this.limitValues[section].min) {
        numberValue = numberValue - 1;
      }
      this.stringValue[section] = this.padForTwoDigits(numberValue.toString());
      this.updateTime(time, section);
    }
  }

  disableSetTimeButton() {
    return this.showLoading || !this.isEditingMode;
  }

  savejobData() {
    this.showLoading = true;
    const title = this._i18nService.instant("MAINTENANCE.TITLE_MAINTENANCE_CASES");
    const message = this._i18nService.instant("MAINTENANCE.START_MAINTENANCE_CASE");
    const button = this._i18nService.instant("GLOBAL.UPDATE");
    const modal = this._modalService.open(ActionConfirmationComponent, {
      closeOnBackdropClick: false,
      closeOnRouterNavigation: true,
      data: { title, message, type: ConfirmationType.CONFIRMATION, button }
    });
    modal.afterClosed().subscribe((result) => {
      if (result === ConfirmationType.CONFIRMATION) {
        this.confirmSavejobData();
      }
      this.showLoading = false;
    });
  }

  confirmSavejobData() {
    const startMinutes = String(DateUtil.calculateMinutesToShowOrSave(this.startTime.minutes, false)).padStart(2, '0');
    const endMinutes = String(DateUtil.calculateMinutesToShowOrSave(this.endTime.minutes, false)).padStart(2, '0');

    const startHours = String(DateUtil.calculateHoursToShowOrSave(this.startTime.hours, false)).padStart(2, '0');
    const endHours = String(DateUtil.calculateHoursToShowOrSave(this.endTime.hours, false)).padStart(2, '0');

    const jobData: IJobData = {
      Enabled: this.jobDataEnabled,
      Mode: this.jobDataMode,
      StartTime: `${startHours}:${startMinutes}`,
      EndTime: `${endHours}:${endMinutes}`
    };

    this.maintenanceService.updateCleanCasesJobData(jobData).subscribe((response) => {
      const message = this._i18nService.instant("MAINTENANCE.CASES_SAVED_SUCCESSFULLY");
      this._toastService.open(message, {
        type: "success",
        duration: 5000,
        verticalPosition: "bottom",
        horizontalPosition: "right"
      });
      this.initialIntervalStartTime.hours = this.startTime.hours;
      this.initialIntervalStartTime.minutes = this.startTime.minutes;
      this.initialIntervalEndTime.hours = this.endTime.hours;
      this.initialIntervalEndTime.minutes = this.endTime.minutes;
    }, (err) => {
      const message = this._i18nService.instant("GLOBAL.GENERIC_ERROR_MSG");
      this._toastService.open(message, {
        type: "error",
        duration: 0,
        verticalPosition: "bottom",
        horizontalPosition: "right"
      });
    });
  }

  get isEndTimeAfterStartTime() {
    if (this.startTime.hours < this.endTime.hours ||
      (this.startTime.hours === this.endTime.hours && this.startTime.minutes < this.endTime.minutes)
    ) {
      return true;
    }
    return false;
  }

  setCurrentStatus() {
    if (this.jobDataMode.toLowerCase() === 'online') {
      this.currentStatus = 'Online';
    } else {
      if (this.jobDataEnabled) {
        this.currentStatus = 'Enabled';
      } else {
        this.currentStatus = 'Disabled';
      }
    }
  }

  setNewInterval() {
    this.isEditingMode = !this.isEditingMode;
  }
}
